import React, { useEffect, useContext, useState, useRef, useMemo, useCallback } from 'react'
import { View, StyleSheet, ScrollView, KeyboardAvoidingView, SafeAreaView, Platform, TouchableOpacity, Dimensions } from 'react-native'
import { Button, Text, Spinner, Icon, Modal, Datepicker, Input, useTheme } from '@ui-kitten/components'
import { UserContext } from '../../global/UserProvider'
import { saveNewSale, updateSale, saveSavedCart, deleteSavedCart, updateSavedCart, updateStoreWiponCredentials } from '../../global/Firebase/firebase'
import { saveFiscalOperation, checkLKTokens, setNewLKTokens, loadLKTokensFromCache, getCurrentShifts, createShift } from '../../global/lightkassa'
import { fiscalizeSale, refreshCredentials, getProfiles } from '../../global/wipon'
 
import HeaderIconButton from '../../components/HeaderIconButton'
import { CartView, totalCartPrice, totalCartPurchasePrice, finalTotalCartPrice, finalItemPrice, discountPrice, bonusReductionValue, bonusAccumulationValue, punchCardReduction, totalCartPriceWithPriceModificators, itemPriceWithoutModifiers, totalCartQuantity } from '../../components/CartView'
import { parseMoneyInput } from '../../utils/cartPricing'
import { parseDecimalAmount } from '../../utils/decimalInput'
import { ClientView } from '../../components/ClientView'
import ClientAutoComplete from '../../components/ClientAutoComplete'
import { PriceTypeSelect } from '../../components/PriceTypeSelect'
import { suggestPriceType, getDefaultPriceType, hasResolvableProductPrice } from '../../utils/priceTypeHelpers'
import { deliveryHasDestination } from '../../utils/deliveryHelpers'
 
import { SavedCartsModal } from '../../components/SavedCartsModal'
 
import PaymentView from '../../components/PaymentView'
import { ProductGrid } from '../../components/ProductGrid'
import { DiscountSelect } from '../../components/DiscountSelect'
import base64 from '../../utils/base64'
import { safePrintAsync } from '../../utils/printUtils'
import { calculateTotalPurchasePriceFromIngredients } from '../../utils/ingredientCalculations'
import { canSellOutOfStock, findInsufficientStockLine } from '../../utils/outOfStockGuard'
import { addStockShortage, cartDrawOnProduct, isReceivingFormOnTop, productStoreStock, receivingParams, RECEIVING_ROUTE } from '../../utils/stockShortage'
import { StockShortageModal } from '../../components/StockShortageModal'
import { captureStockGuidanceReceivingOpened, captureStockGuidanceShown, stockGuidanceRouteMarker } from '../../utils/stockGuidanceAnalytics'
import * as Sentry from "@sentry/react-native"
import { useColors } from '../../utils/useColors'
import { useClientOrderCartSeed } from './useClientOrderCartSeed'
import { CreateClientView } from '../../components/CreateClientView'
import StyledTextInput from '../../components/StyledTextInput'
import { NewSaleSettingsModal } from '../../components/NewSaleSettingsModal'
import { useToast } from "react-native-toast-notifications"
import { sendFBAnalyticsEvent } from '../../global/Firebase/firebaseAnalytics'
import { usePostHog } from 'posthog-react-native'
import { saleCreatedProperties } from '../../global/analytics/saleCreatedProperties'
import { generateInternalReceipt } from '../SalesHistory/SaleDetailScreen'
import { computeCartAddition } from '../../utils/cartAddition'
import { normalizeSavedCarts } from '../../utils/savedCart'
import PlatformDependantSelect from '../../components/PlatformDependantSelect'
import { useTranslation } from 'react-i18next'
import { discountOptionsListAtom, productsDictAtom, productsByBarcodeDictAtom, dailyStatsShiftStatusAtom, usersDictAtom, organizationAtom, storesDictAtom, userRoleAtom, constantsAtom, newSaleInterfaceSettingsAtom, productUnitsDictAtom, currencyFormatterAtom, localeDateServiceAtom, currentStoreSaleSettingsAtom, priceTypesAtom } from '../../global/AtomsProvider'
import { useAtomValue } from 'jotai'
import { getLocalStoreDate, getDailyStatID } from '../../utils/timezoneFunctions'
import { shouldUseSupabase } from '../../global/Firebase/types'
import { createRealtimeChannel, unsubscribeChannel } from '../../global/Supabase/supabaseClient'
import { savedCartsApi } from '../../global/API/typedApiClient'
import { isFiscalizationEnabled } from '../../utils/fiscalization'
import { rememberUnfiscalizedSale, shouldAnnounceFiscalFailure, fiscalFailureDetail, unfiscalizedSaleKey } from '../../utils/unfiscalizedSales'
import { HintView } from '../../components/HintView'
import { findProductByBarcode } from '../../utils/barcodeScan'
import { SCAN_COMPLETE_MS, emptyScanState, nextScanState, completeScanOnPause } from '../../utils/scanBuffer'
import { describeFailedScan } from '../../utils/scanDiagnostics'
import { CHECKOUT_BLOCKED_REASONS, CHECKOUT_BLOCKED_SCREENS, CHECKOUT_BLOCKED_STAGES, captureCheckoutBlocked, classifySaveFailure, isCartEmptyForCheckout } from '../../utils/checkoutBlocked'
import { APP_REVIEW_TRIGGERS, requestAppReviewAtMilestone } from '../../global/AppReview'
import { reconcilePaymentMethod } from '../../utils/defaultPaymentMethod'
import { apiDraftRefusalKey, saleDraftCartSeed } from '../../global/ApiTokens/apiDraftsView'
import { reportApiDraftSaved } from '../../global/ApiTokens/reportApiDraftSaved'
import { deviceDraftReports } from '../../global/ApiTokens/apiDraftReports'
import { SessionOutboxV2Banner } from '../../components/OutboxV2Banner'
import { saveFailureText } from '../../global/Firebase/writeOutcome'

  /*
    cartItem {
      quantity
      salePrice
      finalItemPrice (with discount and quantity)
      itemDiscount
      purchasePrice
      ...product
    }
  */


 

const NewSaleScreen = React.memo(function NewSaleScreen( { navigation, route } ) {
  const { t } = useTranslation()
  const colors = useColors()
  const theme = useTheme()
  const clientOrderDetail = route.params?.clientOrderDetail
  // ATL-1445: an Atlas API v1 sale draft an AI agent proposed, opened from Settings → Agent drafts.
  const apiSaleDraft = route.params?.apiSaleDraft
  const [context, setContext] = useContext(UserContext)
  const productsDict = useAtomValue(productsDictAtom)
  // Route params retain callbacks for the lifetime of the pushed screen. Keep the catalogue
  // behind a ref so AddCartItem can call the SAME function after a realtime update and still
  // validate against the newest products (ATL-1366).
  const productsDictRef = useRef(productsDict)
  productsDictRef.current = productsDict
  const productsByBarcodeAtomValue = useAtomValue(productsByBarcodeDictAtom)
  const productsByBarcode = Platform.OS === 'web' ? productsByBarcodeAtomValue : {}
  const dailyStatsShiftStatus = useAtomValue(dailyStatsShiftStatusAtom)
  const newSaleInterfaceSettings = useAtomValue(newSaleInterfaceSettingsAtom)
  const currentStoreSaleSettings = useAtomValue(currentStoreSaleSettingsAtom)
  const usersDict = useAtomValue(usersDictAtom)
  const organization = useAtomValue(organizationAtom)
  const storesDict = useAtomValue(storesDictAtom)
  const userRole = useAtomValue(userRoleAtom)
  const constants = useAtomValue(constantsAtom)
  const currencyFormatter = useAtomValue(currencyFormatterAtom)
  const productUnitsDict = useAtomValue(productUnitsDictAtom)
  const localeDateService = useAtomValue(localeDateServiceAtom)
  const db = context.db
  const [cartItems, setCartItems] = useState([])
  // ADS-FIX-2026-003: `addNewCartItem` is deliberately closure-free over the cart (see the note on
  // its updater), but a cart-stage refusal must say how much the cart would draw in TOTAL.
  const cartItemsRef = useRef(cartItems)
  cartItemsRef.current = cartItems
  const [paymentMethod, setPaymentMethod] = useState(null)
  const [purchaseMethod, setPurchaseMethod] = useState(constants?.purchaseMethods?.data?.[0])
  const [discountMenuVisible, setDiscountMenuVisible] = useState(false)
  const [discountSelectedIndex, setDiscountSelectedIndex] = useState(0)
  const [showDiscountAmountInputPopup, setShowDiscountAmountInputPopup] = useState(false)
  const [discountAmount, setDiscountAmount] = useState(0)
  const [saveError, setSaveError] = useState(null)
  // ADS-FIX-2026-003: the products a `stock_unavailable` refusal is about, shown with their
  // available and attempted quantities and a path into the acceptance form. Empty = no dialog.
  const [stockShortages, setStockShortages] = useState([])
  // ADS-INSTR-2026-003: which stage the dialog's refusal came from, for its analytics only. A ref,
  // because nothing on screen depends on it; set beside every `setStockShortages` that opens it.
  const stockGuidanceStageRef = useRef(null)
  // The stage as it was when the dialog BECAME visible. A later refusal can add a row to the open
  // dialog (a web scanner keeps scanning under it) and move the ref above; `opened` and the form's
  // marker report this one, so they always carry the stage of the `shown` they follow.
  const stockGuidanceShownStageRef = useRef(null)
  const [comment, setComment] = useState('')
  const [tableNumber, setTableNumber] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [cartMenuVisible, setCartMenuVisible] = useState(false)
  const [createClientModalVisible, setCreateClientModalVisible] = useState(false)
  const [printChecks, setPrintChecks] = useState(constants?.printChecks?.value)
  const [client, setClient] = useState()
  const [bonusAction, setBonusAction] = useState(0)
  const [postPaid, setPostPaid] = useState()
  const [mixedPayment, setMixedPayment] = useState()
  const [delivery, setDelivery] = useState() 
  const [deliveryPrice, setDeliveryPrice] = useState(currentStoreSaleSettings?.deliveryPrice)
  const [deliveryPaidByCustomer, setDeliveryPaidByCustomer] = useState(currentStoreSaleSettings?.deliveryPaidByCustomer ?? true)
  const [deliveryIncludeInReceipt, setDeliveryIncludeInReceipt] = useState(currentStoreSaleSettings?.deliveryIncludeInReceipt ?? true)
  const [deliveryAddress, setDeliveryAddress] = useState('')
  const [deliveryPaymentSourceWallet, setDeliveryPaymentSourceWallet] = useState(currentStoreSaleSettings?.deliveryPaymentSourceWallet)
  const [secondPaymentMethod, setSecondPaymentMethod] = useState(null)
  const [firstPaymentValue, setFirstPaymentValue] = useState('')
  const [savedCartApplied, setSavedCartApplied] = useState(null)
  // ATL-1445: the API draft the cart was seeded from, until the cart is sold, parked or cleared —
  // the same lifetime as `savedCartApplied`. Only a sale saved while it is set is reported back.
  const [apiSaleDraftApplied, setApiSaleDraftApplied] = useState(null)
  const [paymentModalVisible, setPaymentModalVisible] = useState(false)
  const [settingsModalVisible, setSettingsModalVisible] = useState(false)
  const [saleDate, setSaleDate] = useState(null)
  const [store, setStore] = useState(context.user.store?.id)
  const discountOptions = useAtomValue(discountOptionsListAtom)
  const clearClient = useCallback(() => setClient(null), [])

  // ADS-FIX-2026-002: the directory can be loaded while checkout is still blocked because this
  // screen's selection starts (and resets after every sale) at null. Select the sole active method,
  // or the sole cash method (the creation default). Ambiguous lists stay unselected; with none,
  // PaymentMethodsSelect keeps the existing Settings nudge. Nothing is persisted here. Resolving
  // by id on every directory change also prevents a deleted/stale method reaching saveNewSale.
  useEffect(() => {
    setPaymentMethod(current => reconcilePaymentMethod(constants?.paymentMethods?.data, store, current))
  }, [constants?.paymentMethods?.data, store, paymentMethod])
  
  // Handler for manual price type selection
  const handlePriceTypeSelect = useCallback((priceType) => {
    setSelectedPriceType(priceType)
    setManualPriceTypeSelection(true)
  }, [])
  
  // Price type selection
  const priceTypes = useAtomValue(priceTypesAtom)
  const [selectedPriceType, setSelectedPriceType] = useState(null)
  const [manualPriceTypeSelection, setManualPriceTypeSelection] = useState(false)
  
  // Create dynamic discount options that include client's loyalty discount if needed
  const dynamicDiscountOptions = useMemo(() => {
    if (!client?.loyaltyDiscount?.percentage) {
      return discountOptions
    }
    
    const loyaltyPercentage = client.loyaltyDiscount.percentage
    const existingIndex = discountOptions.findIndex(option => option.percentage === loyaltyPercentage)
    
    if (existingIndex === -1) {
      // Add the client's loyalty discount to the options
      const loyaltyDiscountOption = {
        title: `${loyaltyPercentage}% (${t('clients.loyaltyDiscount')})`,
        text: `${loyaltyPercentage}%`,
        percentage: loyaltyPercentage
      }
      return [...discountOptions, loyaltyDiscountOption]
    }
    
    return discountOptions
  }, [client?.loyaltyDiscount?.percentage, discountOptions, t])

  // Removed unused cartCalculations to satisfy linter

  // Memoize loyalty settings
  const loyaltySettings = useMemo(() => ({
    cashbackEnabled: constants?.loyaltySettings?.cashbackEnabled,
    punchCardEnabled: constants?.loyaltySettings?.punchCardEnabled,
    punchCardCapacity: constants?.loyaltySettings?.punchCardCapacity
  }), [constants?.loyaltySettings])

  const toast = useToast()
  const postHog = usePostHog()

  /**
   * ATL-1319 — say WHY the till refused, once, in a normalized shape.
   *
   * Declared here, above every callback that uses it, because `addNewCartItem` is the first of them
   * and reading a `const` declared later would hit the temporal dead zone during render.
   *
   * Defaults to the checkout stage: the cart-stage call sites pass `CART` explicitly, so a new
   * refusal branch added inside `submit()` — which is where nearly all of them are — cannot be
   * mis-staged by forgetting an argument. `captureCheckoutBlocked` swallows everything; see its
   * docblock for why a reporter on this path may never throw.
   */
  const reportCheckoutBlocked = useCallback((reason, stage = CHECKOUT_BLOCKED_STAGES.CHECKOUT) => {
    captureCheckoutBlocked(postHog, reason, { screen: CHECKOUT_BLOCKED_SCREENS.NEW_SALE, stage })
  }, [postHog])

  const _datePicker = React.createRef()
  // ATL-1261: the scan buffer is a ref, not state. It was `useState` + a ref mirrored during
  // render, which re-rendered the whole New Sale screen once per scanned CHARACTER to feed a
  // value nothing ever displayed — and made the handler read a `barcodeRef` that is only
  // refreshed by that render, a race a fast wedge burst can lose. Nothing renders it.
  const scanStateRef = useRef(emptyScanState())
  // ATL-1262/1263: keystrokes of the burst in flight, kept long enough to diagnose a scan that
  // fails, plus the timer that completes a burst no Enter ever closed.
  const scanRecordsRef = useRef([])
  const scanPauseTimerRef = useRef(null)
  const mergeSimilarItems = useRef()

  async function OFDStart() {
    if (storesDict?.[store]?.fiscalOperator?.id === 'light-kassa') {
      await loadLKTokensFromCache(store)
      await checkLKTokens(storesDict?.[store].LKCredentials, store)
      const shifts = await getCurrentShifts()
      if (shifts && shifts.length == 0) {
        await createShift(storesDict?.[store].LKCredentials)
      }
    } else if (storesDict?.[store]?.fiscalOperator?.id === 'wipon-kassa') {
      // check if token is valid
      let profiles = await getProfiles(storesDict?.[store]?.wiponCredentials)
      if (!profiles) {
        // ATL-1170: this used to write `{...credentials, accessToken: await getAccessToken(...)}`
        // unconditionally. `getAccessToken` resolves to `undefined` when the login fails, so during
        // the outage every till that opened this screen wrote a credential blob with no token back
        // over one that was fine — and `update()` replaces the whole map, so the key was dropped,
        // not merged. That is measured, not inferred: 10 of the fleet's 23 wipon stores had lost
        // `accessToken` by 05:02 UTC on 2026-09-04, one of them between two probes 15 minutes
        // apart. `refreshCredentials` returns null when the login fails, and null is not written.
        const refreshed = await refreshCredentials(storesDict?.[store]?.wiponCredentials)
        if (refreshed) {
          try {
            await updateStoreWiponCredentials(storesDict?.[store]?.ref, refreshed)
          } catch (error) {
            Sentry.captureException(error, { tags: { books_write_error: 'store-wipon-credentials-update' } })
          }
        }
      }
    }
  }

  const clearCart = useCallback(() => {
    setCartItems([])
    setSavedCartApplied(null)
    setApiSaleDraftApplied(null)
    setManualPriceTypeSelection(false)
  }, [])

  const navigateHome = useCallback(() => {
    navigation.navigate('Home')
  }, [navigation])

  useEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <HeaderIconButton
          onPress={clearCart}
          iconName='trash-2-outline'
        />
      ),
    })
    if (navigation.getState()?.index === 0) {
      navigation.setOptions({
        headerLeft: () => (
          <HeaderIconButton
            onPress={navigateHome}
            iconName='arrow-back-outline'
          />
        ),
      })
    }
    OFDStart()
    if (!context.unsubscribeSavedCarts) {
      if (shouldUseSupabase()) {
        const fetchSavedCarts = async () => {
          try {
            const carts = await savedCartsApi.getSavedCarts({ storeId: store })
            // Normalise at the read boundary so `cartItems` is an array on every record —
            // the modal's Select button writes it straight into the cart state.
            setContext(context => ({...context, savedCarts: normalizeSavedCarts(carts, 'supabase')}))
          } catch (error) {
            console.error('Error fetching saved carts:', error)
          }
        }
        fetchSavedCarts()

        const supabaseOrgId = organization?.supabaseId
        if (supabaseOrgId) {
          let debounceTimer = null
          const channel = createRealtimeChannel(
            `saved_carts:${supabaseOrgId}`,
            'saved_carts',
            supabaseOrgId,
            () => {
              if (debounceTimer) clearTimeout(debounceTimer)
              debounceTimer = setTimeout(fetchSavedCarts, 300)
            },
            // ATL-1405: a rejoin replays nothing that changed while the channel was down.
            { onStateChange: (state, gap) => { if (state === 'subscribed' && gap) void fetchSavedCarts() } }
          )
          if (channel) {
            setContext(context => ({...context, unsubscribeSavedCarts: () => {
              if (debounceTimer) clearTimeout(debounceTimer)
              unsubscribeChannel(channel)
            }}))
          }
        }
      } else if (context.db) {
        var unsubscribe = context.db.collection('savedCarts').where('store', '==', store).onSnapshot(function(querySnapshot) {
          var docs = []
          querySnapshot.forEach(function(doc) {
            var snap = doc.data()
            snap.id = doc.id
            snap.ref = doc.ref
            docs.push(snap)
          })
          // Same normalisation as the Supabase path above: a Firestore savedCart document
          // with no `cartItems` field otherwise restored `undefined` into the cart state.
          setContext(context => ({...context, savedCarts: normalizeSavedCarts(docs, 'firestore')}))
        })
        setContext(context => ({...context, unsubscribeSavedCarts: unsubscribe}))
      }
    }
    // ATL-1179: the client-order seeding used to live HERE, unguarded, while the saved-carts
    // subscription above is guarded by `if (!context.unsubscribeSavedCarts)`. This effect depends
    // on that same value and assigns it, so it re-triggers itself and the seeding appended the
    // order's lines again — an order for 3 opened New Sale with 6. It now lives in
    // `useClientOrderCartSeed`, below, which is keyed on the order rather than on this effect's
    // dependencies. `clientOrderDetail` is dropped from the list because nothing here reads it.
  }, [clearCart, navigateHome, context.unsubscribeSavedCarts, context.db, store, organization?.supabaseId])

  useEffect(() => {
  if (!store && context.user.store?.id) {
      setStore(context.user.store?.id)
    }
  }, [context.user.store?.id])

  // Define addNewCartItem first to avoid TDZ when used by addItemByBarcode
  const addNewCartItem = useCallback((product, weight=1, isModifier=false, modifiedProductIndex=null) => {
    // ATL-1366: the catalogue views are the first line of defence, but this is the authoritative
    // New Sale boundary. Barcode lookup deliberately indexes the whole catalogue, a row can be
    // tapped while a realtime hide update is replacing it, and AddCartItem can retain a
    // multi-selected object until Confirm. Resolve ordinary products against the CURRENT atom and
    // refuse anything the merchant has now hidden. Besides closing those races, using the current
    // object prevents a stale tap from adding an old price. Custom lines and modifier constants
    // are not catalogue products and keep their existing path.
    const isCatalogueProduct = !isModifier && product?.id !== 'customProduct'
    const currentProductsDict = productsDictRef.current
    if (isCatalogueProduct) {
      // The live catalogue is authoritative even when a retained route callback captured an older item.
      product = currentProductsDict[product?.id]
      if (!product || product.isHiddenInNewSale) return
    }

    // ATL-913: the same permission test as the checkout guard (utils/outOfStockGuard.ts), so the
    // two can no longer drift. This is a de-duplication, NOT a behaviour fix — the inline copy
    // spelled the admin bypass `id === 'admin'`, and that already held under both read modes
    // (the backend's transformRole sets `id` to the firestore id and AppStack normalises it
    // again); see the docblock on `canSellOutOfStock` for why.
    if (!(product.isService) 
          && !(product?.isComposite && !product.manufacturedBeforeSale) 
          && product.storeStock?.[store] <= 0 
          && !canSellOutOfStock(userRole)) {
      // ADS-FIX-2026-003: the refusal stands; what changed is that it now says how much is there,
      // how much was asked for, and how to receive more — instead of a 3-second toast.
      stockGuidanceStageRef.current = CHECKOUT_BLOCKED_STAGES.CART
      setStockShortages(prev => addStockShortage(prev, {
        productId: product.id ?? null,
        productName: product.name ?? null,
        ingredientOf: null,
        available: productStoreStock(product, store),
        attempted: cartDrawOnProduct(cartItemsRef.current, product.id) + Number(weight),
      }))
      // ATL-1319 — a CART-stage refusal: the line never enters the cart, so `submit()` is never
      // reached and the checkout-stage guard below can never see this. Reported at the same stage
      // value as the composite branch below, because to the merchant they are one experience.
      reportCheckoutBlocked(CHECKOUT_BLOCKED_REASONS.STOCK_UNAVAILABLE, CHECKOUT_BLOCKED_STAGES.CART)
      return
    }
    let newProductInfo = {...product}
    if (product?.isComposite) { // composite products - need to count each ingredient separately
      let newIngredients = []
      for (let p of product.ingredients) {
        if (currentProductsDict[p.id]) {
          let newI = {...currentProductsDict[p.id], quantity: p.quantity}
          newIngredients.push(newI)
          if (!product.manufacturedBeforeSale && newI.storeStock?.[store] <= 0 && !canSellOutOfStock(userRole)) {
            // ADS-FIX-2026-003: the INGREDIENT is what is short and what can be received.
            stockGuidanceStageRef.current = CHECKOUT_BLOCKED_STAGES.CART
            setStockShortages(prev => addStockShortage(prev, {
              productId: newI.id ?? null,
              productName: newI.name ?? null,
              ingredientOf: product.name ?? null,
              available: productStoreStock(newI, store),
              // No `|| 0`: an unreadable recipe quantity shows as «—», not as a believable 0.
              // Every recipe entry naming this ingredient counts, as it does at checkout.
              attempted: cartDrawOnProduct(cartItemsRef.current, newI.id) + product.ingredients
                .filter((entry) => entry?.id === newI.id)
                .reduce((sum, entry) => sum + Number(entry.quantity) * Number(weight), 0),
            }))
            // ATL-1319 — same reason as the plain-product branch above. It is deliberately NOT a
            // separate reason: which of a composite's parts ran out is a fact about the product,
            // not about why checkout could not happen, and naming the ingredient would put a
            // merchant's product name into the event.
            reportCheckoutBlocked(CHECKOUT_BLOCKED_REASONS.STOCK_UNAVAILABLE, CHECKOUT_BLOCKED_STAGES.CART)
            return
          }
        }
      }
      newProductInfo.ingredients = newIngredients
      newProductInfo.purchasePrice = calculateTotalPurchasePriceFromIngredients(newIngredients)
    }
    if (isModifier) {
      newProductInfo['isModifier'] = true
    }
    // QARASAY-KASSA-2B2/-2RH: the state transition is a TOTAL pure function (utils/cartAddition)
    // and the "open the weight/price editor" decision comes back as data. The branch that used
    // to navigate and then fall off the end of this updater set the cart to `undefined`, which
    // emptied the cashier's cart silently and then crashed EditCartItemScreen's `[...cartItems]`.
    //
    // `navigation.navigate` still runs inside the updater because `prev` is the only place the
    // pre-add cart is available (addNewCartItem is deliberately closure-free over cartItems: it
    // is handed to EditCartItemScreen through route params, where a stale cart would corrupt a
    // live sale). The app mounts no StrictMode, so the updater is not double-invoked; navigating
    // to a route already on the stack is idempotent in any case. Everything that decides the
    // cart's next value is above it, in the pure function.
    setCartItems(prevCartItems => {
      const { cartItems: nextCartItems, edit } = computeCartAddition(prevCartItems, {
        productInfo: newProductInfo,
        weight,
        isModifier,
        modifiedProductIndex,
        mergeSimilarItems: mergeSimilarItems.current,
        defaultItemDiscount: dynamicDiscountOptions[0],
      })
      if (edit) {
        navigation.navigate('EditCartItem', {
          index: edit.index,
          cartItem: edit.cartItem,
          cartItems: nextCartItems,
          setCartItems,
          store: store,
          addNewCartItem: addNewCartItem,
        })
      }
      return nextCartItems
    })
  }, [store, userRole, t, dynamicDiscountOptions, navigation, mergeSimilarItems.current, reportCheckoutBlocked])

  // using Ref cause it's value is needed in static function
  useEffect(() => {
    mergeSimilarItems.current = currentStoreSaleSettings?.mergeSimilarItems
  }, [currentStoreSaleSettings?.mergeSimilarItems])
  
  useEffect(() => {
    if (currentStoreSaleSettings) {
      if (deliveryPrice === undefined) setDeliveryPrice(currentStoreSaleSettings?.deliveryPrice)
      if (deliveryPaidByCustomer === undefined) setDeliveryPaidByCustomer(currentStoreSaleSettings?.deliveryPaidByCustomer ?? true)
      if (deliveryIncludeInReceipt === undefined) setDeliveryIncludeInReceipt(currentStoreSaleSettings?.deliveryIncludeInReceipt ?? true)
      if (deliveryPaymentSourceWallet === undefined) setDeliveryPaymentSourceWallet(currentStoreSaleSettings?.deliveryPaymentSourceWallet)
    }
  }, [currentStoreSaleSettings?.deliveryPrice, currentStoreSaleSettings?.deliveryPaidByCustomer, currentStoreSaleSettings?.deliveryIncludeInReceipt, currentStoreSaleSettings?.deliveryPaymentSourceWallet, currentStoreSaleSettings])
  
  useEffect(() => {
    if (discountSelectedIndex === 1 && dynamicDiscountOptions[discountSelectedIndex]?.amount === 0) {
      setShowDiscountAmountInputPopup(true)
    }
    if (discountSelectedIndex !== 1) {
      setDiscountAmount(0)
    }
  }, [discountSelectedIndex])

  // Auto-apply client loyalty discount
  useEffect(() => {
    if (client?.loyaltyDiscount) {
      const loyaltyDiscount = client.loyaltyDiscount
      if (loyaltyDiscount.percentage) {
        // Find the discount option with matching percentage in dynamic options
        const discountIndex = dynamicDiscountOptions.findIndex(option => option.percentage === loyaltyDiscount.percentage)
        if (discountIndex !== -1) {
          setDiscountSelectedIndex(discountIndex)
          setDiscountAmount(0)
        }
      } else if (loyaltyDiscount.amount) {
        // For amount discounts, always use index 1 (custom amount) and set the amount
        setDiscountSelectedIndex(1)
        setDiscountAmount(loyaltyDiscount.amount)
      }
    } else if (!client) {
      // Reset discount when no client is selected
      setDiscountSelectedIndex(0)
      setDiscountAmount(0)
    }
  }, [client, dynamicDiscountOptions])

  // Auto-fill delivery address when client is selected
  useEffect(() => {
    if (client?.address && delivery) {
      setDeliveryAddress(client.address)
    }
  }, [client, delivery])

  // Initialize price type (default is null/salePrices)
  useEffect(() => {
    if (!selectedPriceType && priceTypes) {
      const defaultType = getDefaultPriceType()
      setSelectedPriceType(defaultType)
    }
  }, [selectedPriceType, priceTypes])

  // Memoize cart quantity to avoid recalculating on every render
  const cartQuantity = useMemo(() => {
    return totalCartQuantity(cartItems)
  }, [cartItems])

  // Memoize cart total to avoid recalculating on every render
  const cartTotal = useMemo(() => {
    return totalCartPrice(cartItems, store, selectedPriceType?.id, priceTypes)
  }, [cartItems, store, selectedPriceType?.id, priceTypes])

  // Auto-suggest price type based on rules
  useEffect(() => {
    // Don't auto-suggest if user has manually selected a price type
    if (manualPriceTypeSelection) return
    if (!priceTypes || priceTypes.length === 0) return
    
    const suggested = suggestPriceType(cartItems, paymentMethod, client, priceTypes, cartTotal)
    
    // suggested will be null for default (salePrices) or a priceType id for additional prices
    if (suggested === null) {
      // Switch to default pricing
      if (selectedPriceType?.id !== null) {
        setSelectedPriceType(getDefaultPriceType())
      }
    } else if (suggested !== selectedPriceType?.id) {
      // Switch to a different additional price type
      const suggestedType = priceTypes.find(pt => pt.id === suggested)
      if (suggestedType) {
        setSelectedPriceType(suggestedType)
        // Optional: Show a toast notification
        toast.show(t('newSale.priceTypeAutoChanged', { name: suggestedType.name }), { type: 'info' })
      }
    }
  }, [cartQuantity, cartTotal, paymentMethod?.id, client?.preferredPriceType, priceTypes, manualPriceTypeSelection])

  /**
   * ATL-1262: a scan that resolves nothing is otherwise SILENT — `findProductByBarcode` logs to
   * a console nobody is reading and the screen does not change. Report why, so a merchant's
   * "the barcode does not work" can be answered from the event stream instead of a phone call.
   * Never let reporting break a scan: the till keeps selling even if analytics is unavailable.
   */
  const reportFailedScan = useCallback((records, code, terminatedByEnter, found) => {
    try {
      const payload = describeFailedScan({
        records,
        code,
        terminatedByEnter,
        found,
        catalogSize: Object.keys(productsByBarcode).length,
      })
      if (payload) postHog?.capture?.('barcode_scan_failed', { ...payload, store_id: store })
    } catch (error) {
      Sentry.captureException(error, { level: 'warning', tags: { barcode_scan_diagnostics: true } })
    }
  }, [postHog, productsByBarcode, store])

  const addItemByBarcode = useCallback((barcodeInput, records = [], terminatedByEnter = true) => {
    barcodeInput = barcodeInput.length > 13 ? barcodeInput.substring(barcodeInput.length-13, barcodeInput.length) : barcodeInput
    if (barcodeInput.startsWith('27')) {
      // var clientPhone = parseInt(barcodeInput.substring(1, 12))
      // paste into search
    } else {
      let [item, weight] = findProductByBarcode(barcodeInput, productsByBarcode)
      if (item) addNewCartItem(item, weight)
      reportFailedScan(records, barcodeInput, terminatedByEnter, Boolean(item))
    }
  }, [productsByBarcode, addNewCartItem, reportFailedScan])

  const handleScannerInput = useCallback((e) => {
    // ADS-FIX-2026-003: the acceptance form a stock refusal opens sits on top of this (still
    // mounted) screen; its keystrokes and scans are its own, not this cart's.
    if (isReceivingFormOnTop(navigation.getState?.())) return
    e.preventDefault()
    // ATL-1261: `event.key` is the character the scanner actually sent — numpad digits and
    // letter case included — and every non-character key (Shift, arrows, Tab) is dropped
    // rather than appended as `String.fromCharCode(keyCode)` junk. `event.timeStamp` lets
    // the buffer be delimited by the scanner's own burst instead of by a width guess.
    const prev = scanStateRef.current
    const next = nextScanState(prev, { key: e.key, at: e.timeStamp })
    // ATL-1262: the keystrokes are kept only to diagnose a scan that fails, and only for the
    // burst in flight. A burst STARTS when a printable key leaves a one-character buffer — the
    // test is on the key too, because a Shift arriving while the buffer holds one character
    // leaves it at one character without starting anything, and would otherwise drop the
    // record of the character already there.
    const startsBurst = typeof e.key === 'string' && e.key.length === 1 && next.buffer.length === 1
    const records = (startsBurst ? [] : scanRecordsRef.current).concat({
      key: e.key, code: e.code, t: e.timeStamp,
    })
    scanStateRef.current = { buffer: next.buffer, lastAt: next.lastAt }
    scanRecordsRef.current = records
    clearTimeout(scanPauseTimerRef.current)

    if (next.scanned !== null) {
      scanRecordsRef.current = []
      addItemByBarcode(next.scanned, records, true)
      return
    }
    // ATL-1263: Enter is not the only delimiter a scanner gives. It also stops. A scanner
    // configured without an Enter suffix never reached the branch above, so every code it sent
    // was thrown away — measured on a live till as 20 consecutive 13-digit codes, 4ms apart,
    // none of them added to a sale. Completing on the pause makes that scanner work as it is,
    // instead of asking the merchant to reconfigure hardware. `completeScanOnPause` refuses
    // anything a person could have typed; see its docblock for why that is two conditions, not
    // a threshold. The timer is cleared above on every keystroke, so it only ever fires on real
    // silence, and a burst that DOES end in Enter has taken the branch above and left an empty
    // buffer behind — nothing to complete twice.
    scanPauseTimerRef.current = setTimeout(() => {
      const pending = scanRecordsRef.current
      const code = completeScanOnPause(scanStateRef.current)
      scanRecordsRef.current = []
      scanStateRef.current = emptyScanState()
      if (code !== null) addItemByBarcode(code, pending, false)
    }, SCAN_COMPLETE_MS)
  }, [addItemByBarcode, navigation])

  useEffect(() => {
    if (Platform.OS === 'web') {
      document.addEventListener("keydown", handleScannerInput)
    }
    return () => {
      if (Platform.OS === 'web') {
        document.removeEventListener("keydown", handleScannerInput)
      }
      // ATL-1263: a pending completion must not outlive the screen — it would add a cart line
      // to a till that has already left New Sale.
      clearTimeout(scanPauseTimerRef.current)
    }
  }, [handleScannerInput])

  const changeQuantityOfItem = useCallback((quantityDifference, item, index) => {
    setCartItems(cartItems => {
      // No `!Array.isArray(cartItems) -> []` guard here: returning an empty array silently
      // DISCARDED the cashier's cart, which is how QARASAY-KASSA-2B2 stayed invisible.
      // The invariant this relies on is ENFORCED, not assumed: cartItems is initialised to
      // [], every in-app writer goes through computeCartAddition or setCartItems([]), and the
      // one writer that takes external data — SavedCartsModal's Select — is fed by
      // normalizeSavedCarts() at both read paths above (utils/savedCart.js).
      const newCartItems = [...cartItems]
      const updatedCartItem = {...item, quantity: item.quantity+quantityDifference}
      newCartItems.splice(index, 1, updatedCartItem)
      return newCartItems
    })
  }, [])

  // Memoised because it is a DEPENDENCY of `useClientOrderCartSeed`'s effect: rebuilt on every
  // render it would re-run that effect on every render of the busiest screen in the app (every
  // cart change, every keystroke), leaving the ref guard as the only thing standing between the
  // cashier and a doubled cart. The guard still holds either way — this makes the dependency
  // array mean what it says.
  const newSaleFromClientOrder = useCallback(async (clientOrder) => {
    for (let item of clientOrder.cart) {
      addNewCartItem(productsDict[item.id], item.quantity, false, null)
    }
  }, [addNewCartItem, productsDict])

  /*
   * ATL-1179 — seed the cart from a client order exactly once.
   *
   * Called here rather than beside the other effects because `newSaleFromClientOrder` is a `const`
   * declared just above: reading it earlier in the component body would hit the temporal dead zone
   * during render. Hooks must run unconditionally in a stable order, which this does.
   *
   * `productsReady` is the seed's precondition — every line resolves as `productsDict[item.id]`,
   * so seeding before the catalog loads would open an EMPTY sale and never retry.
   */
  const productsReady = useMemo(() => Object.keys(productsDict).length > 0, [productsDict])
  useClientOrderCartSeed({
    clientOrderDetail,
    productsReady,
    seedCart: newSaleFromClientOrder,
  })

  /*
   * ATL-1445 — seed the cart from an API sale draft, through the SAME once-only hook and the SAME
   * append loop as a client order: the draft is only ever a cart the cashier then checks out as
   * usual (price list, payment, receipt, fiscalization — all decided here, none by the agent).
   * The seed key is `api-draft:<id>`, so it can never collide with an order's id.
   *
   * The draft's store is the one the Agent drafts screen checked equals the cashier's store; it is
   * set here too so a NewSale instance mounted before a store switch cannot sell it elsewhere (the
   * backend would refuse to link a sale from another store).
   */
  const apiSaleDraftSeed = useMemo(() => (apiSaleDraft ? saleDraftCartSeed(apiSaleDraft) : null), [apiSaleDraft])
  const seedFromApiSaleDraft = useCallback(async (seed) => {
    setStore(apiSaleDraft.store.id)
    setApiSaleDraftApplied(apiSaleDraft)
    await newSaleFromClientOrder(seed)
  }, [apiSaleDraft, newSaleFromClientOrder])
  useClientOrderCartSeed({
    clientOrderDetail: apiSaleDraftSeed,
    productsReady,
    seedCart: seedFromApiSaleDraft,
  })

  // ATL-1445: a draft sale whose report could not be delivered (a 5xx, Firestore unavailable) stays
  // "open" on the backend — for the agent and for every OTHER device — until it lands. Retrying only
  // from Settings → Agent drafts would leave it open for as long as nobody visits that screen, so
  // the till retries too. Cheap when nothing is pending: one local read, no request. Sale reports
  // only: whoever is on New Sale holds `NewSale`, not necessarily `Supplies`.
  useEffect(() => {
    const organizationId = context.user?.organizationId
    if (!organizationId) return
    deviceDraftReports.retryPending(organizationId, { kinds: ['sale'] }).catch((error) => {
      Sentry.captureException(error, { level: 'warning', tags: { feature: 'api-draft' } })
    })
  }, [context.user?.organizationId])

  /**
   * Print the fiscal receipt the operator returned.
   *
   * ATL-1274 — pulled out of `saveSaleToOFD` and given its own error boundary, because
   * `saveSaleToOFD` now REPORTS its outcome and a print failure must never be reported as a fiscal
   * failure. The wipon branch could throw here on web (`window.open` returns `null` when a popup
   * blocker is on — the light-kassa branch has always guarded that, this one never did), which
   * would have surfaced as «чек не фискализирован» for a receipt the OFD had already signed. The
   * print is not silent either: `safePrintAsync` toasts, anything that throws reaches Sentry, and
   * a blocked popup — the very case the `if (mywindow)` guard exists for, which throws nothing and
   * toasts nothing — is reported explicitly rather than swallowed by the guard.
   *
   * Resolves to whether the print step finished cleanly: `true` when the receipt printed or the
   * store does not print, `false` for a failed or cancelled print, a blocked popup, or a throw.
   * ASO-RATING-2026-001 reads it — a sale whose receipt just failed to print is not a moment to ask
   * for a rating. It changes nothing about how the failure itself is reported.
   */
  const printFiscalReceipt = async (buildReceiptBody) => {
    if (!printChecks) return true
    try {
      // Called inside the try, not before it: decoding the operator's base64 receipt is itself a
      // thing that can throw (an operator that returned no `receipt` field at all), and it used to
      // run only when `printChecks` was on. Keeping it lazy preserves both properties.
      const html = '<html><head><title>' + t('newSale.receipt') + '</title>' + '<style> div {white-space:pre-wrap;} </style>' + '</head><body >'
        + '<div>' + buildReceiptBody() + '</div>' + '</body></html>'
      if (Platform.OS === 'web') {
        const mywindow = window.open('', 'PRINT', 'height=400,width=600')
        if (mywindow) {
          mywindow.document.write(html)
          mywindow.document.close()
          mywindow.focus()
          mywindow.onafterprint = function() {mywindow.close()}
          mywindow.print()
          return true
        } else {
          // A popup blocker. Guarding the null was right — throwing here used to report a SIGNED
          // receipt as a fiscal failure — but the guard on its own makes the receipt silently not
          // print, which is the one thing the docstring above promises it is not.
          Sentry.captureMessage('Fiscal receipt print window was blocked', {
            level: 'warning',
            tags: {fiscalOperator: storesDict?.[store]?.fiscalOperator?.id},
            extra: {store},
          })
          toast.show(t('newSale.receiptPrintBlocked'), {type: 'warning', placement: 'bottom', duration: 4000})
          return false
        }
      } else {
        return await safePrintAsync({ html }, { toast, t })
      }
    } catch (error) {
      Sentry.captureException(error)
      return false
    }
  }

  /**
   * The sales this till knows are saved but not fiscalized — see `utils/unfiscalizedSales.ts`.
   *
   * Held in the app-wide context rather than in this screen's state so that walking to Sales
   * History and back does not erase it, and so the two retry paths over there can prune it as they
   * sign. `unfiscalizedRef` mirrors it for the announcement decision below, which has to read the
   * register as it stands at the moment of a failure: two sales in a rush can both fail before
   * React has re-rendered either of them, and reading `context` from the closure would then let
   * the second one announce a refusal the first has already announced.
   */
  const unfiscalizedSales = useMemo(() => context.unfiscalizedSales || [], [context.unfiscalizedSales])
  const unfiscalizedRef = useRef(unfiscalizedSales)
  useEffect(() => { unfiscalizedRef.current = unfiscalizedSales }, [unfiscalizedSales])

  /**
   * ATL-1274 — report a fiscalization failure without stopping the queue.
   *
   * Two surfaces, and the split is the whole design. The BANNER is persistent: it stays until the
   * sales behind it are signed, so a failure cannot be missed by looking away for five seconds,
   * and it carries the route to the screen that can actually re-sign them. The TOAST is the
   * announcement, and it is deliberately rationed — a cashbox that is blocked refuses every sale
   * of the shift, and thirteen identical interruptions (which is what Petit Зенкова got, twice) is
   * not thirteen times the information. A failure that says something NEW is announced again.
   */
  const reportFiscalizationFailure = (outcome) => {
    const entry = {
      saleId: outcome?.saleId ?? null,
      reason: outcome?.reason || 'rejected',
      message: outcome?.message || null,
      at: Date.now(),
    }
    const announce = shouldAnnounceFiscalFailure(unfiscalizedRef.current, entry)
    unfiscalizedRef.current = rememberUnfiscalizedSale(unfiscalizedRef.current, entry)
    setContext(ctx => ({...ctx, unfiscalizedSales: rememberUnfiscalizedSale(ctx.unfiscalizedSales, entry)}))
    if (!announce) return
    const detail = fiscalFailureDetail(entry, t)
    const headline = detail
      ? t('newSale.receiptNotFiscalizedWithReason', {reason: detail})
      : t('newSale.receiptNotFiscalized')
    toast.show(headline + '\n' + t('newSale.tapToOpenSalesHistory'), {
      type: 'danger',
      placement: 'bottom',
      duration: 6000,
      offset: 30,
      animationType: 'slide-in',
      onPress: () => navigation.navigate('SalesHistory'),
    })
  }

  /**
   * Sign the sale with the store's fiscal operator, and SAY WHAT HAPPENED.
   *
   * ATL-1274 — this used to resolve to `undefined` whatever it did, so the only way a failure
   * reached the cashier was the blocking `alert()` that `global/wipon.js` raised from inside its
   * own error handler: once per sale, on the checkout screen, for a sale that had already been
   * saved. It now returns `{fiscalized, reason, message, saleId}` and the caller renders it beside
   * the saved-sale toast. Light Kassa is included even though it has never alerted at all — its
   * failures were completely silent, which is the same defect wearing the other mask.
   *
   * It still does not THROW on a failed sign, and the caller still does not await it: the cashier
   * must not wait on the OFD, and ATL-1170's contract ("a failed sign is reported, never thrown")
   * is what keeps an unawaited call from becoming an unhandled rejection.
   */
  const saveSaleToOFD = async (cartInfo, ref) => {
    const saleId = unfiscalizedSaleKey(ref)
    if (storesDict?.[store].fiscalOperator?.id === 'light-kassa') {
      let fiscalInfo = await saveFiscalOperation(storesDict?.[store].LKCredentials, cartInfo, store)
      if (!fiscalInfo) {
        await setNewLKTokens(storesDict?.[store].LKCredentials, store)
        fiscalInfo = await saveFiscalOperation(storesDict?.[store].LKCredentials, cartInfo, store)
      }
      if (!fiscalInfo) {
        // Light Kassa reports no reason of its own — `saveFiscalOperation` resolves falsy and that
        // is all there is. `message: null` is the honest answer, and the UI shows the headline
        // without inventing a cause for it.
        return {fiscalized: false, reason: 'rejected', message: null, saleId}
      }
      try {
        await updateSale(ref, {fiscalInfo})
      } catch(error) {
        Sentry.captureException(error, { tags: { books_write_error: 'sale-fiscal-info-update-light-kassa' } })
      }
      const receiptPrinted = await printFiscalReceipt(() => decodeURIComponent(escape(
        Platform.OS === 'web' ? atob(fiscalInfo.receipt) : base64.decode(fiscalInfo.receipt),
      )))
      return {fiscalized: true, reason: null, message: null, saleId, receiptPrinted}
    } else if (storesDict?.[store].fiscalOperator?.id === 'wipon-kassa') {
      // ATL-1170: one attempt, and a swallowed failure — `saveSale` resolves to `undefined` on any
      // error, so `if (fiscalInfo)` skipped the receipt in silence. `fiscalizeSale` re-issues the
      // token and retries once (what the light-kassa branch above has always done), and reports why
      // it gave up so the failure is visible in Sentry rather than in tomorrow's sales list.
      const {fiscalInfo, reason, message, responseClass, rejection} = await fiscalizeSale(
        storesDict?.[store]?.wiponCredentials,
        cartInfo,
        (refreshed) => updateStoreWiponCredentials(storesDict?.[store]?.ref, refreshed),
      )
      if (!fiscalInfo) {
        Sentry.captureException(new Error(`Wipon fiscalization failed: ${reason}`), {
          tags: {fiscalOperator: 'wipon-kassa', fiscalFailureReason: reason, fiscalResponseClass: responseClass || 'none', fiscalResponseCode: rejection?.code || 'none'},
          extra: {store, saleRef: ref?.id, organization: organization?.id, wiponRejection: rejection},
        })
        return {fiscalized: false, reason, message, saleId}
      }
      try {
        await updateSale(ref, {fiscalInfo})
      } catch(error) {
        Sentry.captureException(error, { tags: { books_write_error: 'sale-fiscal-info-update-wipon' } })
      }
      const receiptPrinted = await printFiscalReceipt(() => fiscalInfo.check)
      return {fiscalized: true, reason: null, message: null, saleId, receiptPrinted}
    }
    // A store configured to an operator this app cannot talk to. `isFiscalizationEnabled` counts it
    // as fiscalizing (deliberately — ATL-1020), and nothing here signed anything, so it is exactly
    // as unfiscalized as a refusal and is reported as one rather than reading as success. It gets
    // its OWN reason: the operator is configured, it is just not one we speak, and telling the
    // merchant it is "not configured" would send them to fix a setting that is already set.
    //
    // Review raised that no retry path can ever clear these, so the banner would stay up for the
    // life of the session on such a store. That is left deliberately, because it is TRUE: those
    // sales are permanently unsigned, Sales History's own data-derived banner says exactly the
    // same thing and its batch retry pushes them straight to `failedSales`, and the toast does not
    // repeat (the failure signature is identical every time). A dismiss affordance here would let
    // a real, permanent fiscal gap be waved away, which is the outcome this ticket forbids.
    // `StoreDetailScreen` ships only none/light-kassa/wipon-kassa, so this is defensive in
    // practice — it exists because the operator id is stored data, not a closed enum.
    return {fiscalized: false, reason: 'unsupported-operator', message: null, saleId}
  }

  const handleDeleteSavedCart = useCallback(async (item) => {
    const itemId = item.id || item.ref?.id
    // Optimistically remove from list
    setContext(ctx => ({...ctx, savedCarts: (ctx.savedCarts || []).filter(c => (c.id || c.ref?.id) !== itemId)}))
    await deleteSavedCart(item.ref || item)
  }, [setContext])

  const saveCart = useCallback(async () => {
    if (cartItems.length > 0) {
      setIsLoading(true)
      // ATL-867: `discountAmount` is a TEXT INPUT — it is the number 0 until the cashier types, then
      // a string. Sending the raw state made the backend refuse the saved cart with a 400
      // ("discountAmount must be a number"), which the dual-write classifies as a DETERMINISTIC
      // defect: not queued, not retried, and Firestore had already committed. Parse once here so
      // BOTH stores receive the same number, and refuse an unreadable one the way deliveryPrice
      // below already does rather than inventing a value for a money field.
      const discountAmountNumber = parseMoneyInput(discountAmount)
      if (discountAmountNumber === null) {
        setIsLoading(false)
        setSaveError(t('notANumberError'))
        return
      }
      let savedCartInfo = {
        cartItems: cartItems,
        client: client,
        discountSelectedIndex: discountSelectedIndex,
        discountAmount: discountAmountNumber,
        bonusAction: bonusAction,
        selectedPriceType: selectedPriceType,
        finalTotalCartPrice: finalTotalCartPrice(dynamicDiscountOptions[discountSelectedIndex]?.percentage, discountAmountNumber, cartItems, store, client, bonusAction, constants?.loyaltySettings?.cashbackEnabled, constants?.loyaltySettings?.punchCardEnabled, constants?.loyaltySettings?.punchCardCapacity, purchaseMethod?.priceModificators, selectedPriceType?.id || null, priceTypes),
        cashierUID: context.user.uid,
        tableNumber,
        comment,
        store: store,
        dateModified: new Date(),
        dateCreated: (savedCartApplied?.dateCreated || new Date()),
        organizationId: context.user.organizationId
      }
      if (paymentMethod) {
        savedCartInfo = {...savedCartInfo, paymentMethod}
      }
      if (delivery) {
        // ATL-1371 — the SAME state as the checkout read below, so it must accept the same set.
        // Converting only checkout meant a saved cart took '500abc' as 500 and wrote it to both
        // stores, and `SavedCartsModal` fed that 500 back in on apply — one box, two answers.
        const numberAmount = parseDecimalAmount(deliveryPrice)
        if (numberAmount === null) {
          setIsLoading(false)
          setSaveError(t('notANumberError'))
          return
        }
        savedCartInfo = {...savedCartInfo,
          delivery,
          deliveryPrice: numberAmount,
          deliveryPaidByCustomer,
          deliveryAddress,
          deliveryIncludeInReceipt,
          deliveryPaymentSourceWallet
        }
      }
      Object.keys(savedCartInfo).forEach((k) => savedCartInfo[k] == null && delete savedCartInfo[k]);
      if (savedCartApplied) {
        await updateSavedCart(savedCartApplied, savedCartInfo)
      } else {
        await saveSavedCart(context.db, savedCartInfo)
      }
      setCartItems([])
      setClient(null)
      setTableNumber('')
      setDelivery(null)
      setDeliveryPrice(currentStoreSaleSettings?.deliveryPrice)
      setDeliveryAddress('')
      setDeliveryPaidByCustomer(currentStoreSaleSettings?.deliveryPaidByCustomer ?? true)
      setDeliveryIncludeInReceipt(currentStoreSaleSettings?.deliveryIncludeInReceipt ?? true)
      setDeliveryPaymentSourceWallet(currentStoreSaleSettings?.deliveryPaymentSourceWallet)
      setComment('')
      setBonusAction(0)
      setDiscountSelectedIndex(0)
      setDiscountAmount(0)
      setSavedCartApplied(null)
      // A parked cart is no longer the draft: restoring it later is an ordinary sale.
      setApiSaleDraftApplied(null)
      setIsLoading(false)
      setPaymentModalVisible(false)
      setPaymentMethod(null)
      setPostPaid(null)
      setMixedPayment(null)
      setSecondPaymentMethod(null)
      setFirstPaymentValue('')
      setManualPriceTypeSelection(false)
    }
  }, [cartItems, client, discountSelectedIndex, discountAmount, bonusAction, selectedPriceType, constants, tableNumber, comment, store, context.user.uid, context.user.organizationId, savedCartApplied, delivery, deliveryPrice, deliveryPaidByCustomer, deliveryAddress, deliveryIncludeInReceipt, deliveryPaymentSourceWallet, paymentMethod, newSaleInterfaceSettings, t, db, saveSavedCart])

  // ADS-FIX-2026-001: an empty cart has nothing to check out — the CTAs are disabled on the same
  // predicate `submit()` refuses on, instead of letting the cashier tap into a refusal.
  const cartIsEmpty = isCartEmptyForCheckout(cartItems)

  // ADS-FIX-2026-003: the way out of a `stock_unavailable` refusal. Same permission test as the
  // «Создать приёмку» button on the product stock screen. The acceptance form is pushed ON TOP of
  // New Sale, so this screen — and with it the cart, client and payment state — stays mounted, and
  // back/Esc or the form's own «Назад» return to it intact. Nothing is written here: the form saves
  // only when the cashier confirms it there.
  const canReceiveStock = Boolean(userRole?.permissions?.['Supplies'] || context.user.role === 'admin')
  const isStockShortageReceivable = useCallback(
    (shortage) => receivingParams(shortage, productsDict, store) !== null,
    [productsDict, store],
  )
  const closeStockShortages = useCallback(() => setStockShortages([]), [])
  // ADS-INSTR-2026-003: the dialog was rendered visible — the proof this device runs ADS-FIX-2026-003.
  const reportStockGuidanceShown = useCallback(({ receivingOffered }) => {
    stockGuidanceShownStageRef.current = stockGuidanceStageRef.current
    captureStockGuidanceShown(postHog, { stage: stockGuidanceShownStageRef.current, receivingOffered })
  }, [postHog])
  const openStockReceiving = useCallback((shortage) => {
    if (!canReceiveStock) return
    // One product per acceptance — see `receivingParams` for why a mixed-supplier prefill is wrong.
    const params = receivingParams(shortage, productsDict, store)
    if (!params) return
    // ADS-INSTR-2026-003: reported only once the form is actually going to open. The marker tells the
    // form it came from here, so its Save — and only that — reports `receiving_confirmed`. The prefill
    // itself is `receivingParams`, unchanged.
    const stage = stockGuidanceShownStageRef.current
    captureStockGuidanceReceivingOpened(postHog, { stage })
    setStockShortages([])
    // The stale refusal line would still be on screen after the acceptance is saved.
    setSaveError(null)
    // checkoutStyle 2 refuses from inside the payment modal, which would otherwise stay on top of
    // the acceptance form. Its fields are screen state and survive closing it.
    setPaymentModalVisible(false)
    navigation.navigate(RECEIVING_ROUTE, { ...params, stockGuidance: stockGuidanceRouteMarker(stage) })
  }, [canReceiveStock, productsDict, store, navigation, postHog])

  const submit = useCallback(async () => {
    if (!userRole?.permissions?.['newSaleWithoutShift'] && context.user.role !== 'admin' && 
      (!dailyStatsShiftStatus[store]?.shiftOpened || (dailyStatsShiftStatus[store]?.shiftOpened && dailyStatsShiftStatus[store]?.shiftClosed))) {
      setSaveError(t('newSale.shiftNotOpenedError'))
      reportCheckoutBlocked(CHECKOUT_BLOCKED_REASONS.SHIFT_NOT_OPEN)
      return
    }
    if (purchaseMethod === null) {
      setSaveError(t('newSale.choosePurchaseMethodError'))
      setIsLoading(false)
      reportCheckoutBlocked(CHECKOUT_BLOCKED_REASONS.PURCHASE_METHOD_MISSING)
      return
    }
    // ADS-FIX-2026-001: the checkout CTAs are disabled on this same predicate, so no UI path should
    // reach this branch any more. It stays as the refusal of last resort — see isCartEmptyForCheckout.
    if (isCartEmptyForCheckout(cartItems)) {
      setSaveError(t('newSale.cartItemsCannotBeEmpty'))
      setIsLoading(false)
      reportCheckoutBlocked(CHECKOUT_BLOCKED_REASONS.CART_EMPTY)
      return
    }
    if (!((paymentMethod != null || (paymentMethod == null && postPaid)))) {
      setSaveError(t('newSale.choosePaymentMethodError'))
      setIsLoading(false)
      reportCheckoutBlocked(CHECKOUT_BLOCKED_REASONS.PAYMENT_METHOD_MISSING)
      return
    }
    setSaveError(null)
    setIsLoading(true)
    // ATL-913: decide the out-of-stock permission against CURRENT stock, not against the copy
    // frozen into the cart line when the product was tapped. A cart that sat open while another
    // till sold the same product used to pass this check on minutes-old numbers and commit a sale
    // below zero. `productsDict` is the live products listener; see utils/outOfStockGuard.ts.
    const insufficientStockLine = findInsufficientStockLine({ cartItems, productsDict, store, userRole })
    if (insufficientStockLine) {
      setSaveError(insufficientStockLine.kind === 'ingredient'
        ? t('newSale.cannotSellOutOfStockIngredients', {ingredientName: insufficientStockLine.ingredientName, productName: insufficientStockLine.productName})
        : t('newSale.cannotSellOutOfStock', {productName: insufficientStockLine.productName}))
      setIsLoading(false)
      // ADS-FIX-2026-003: name the short product with the numbers the guard compared.
      stockGuidanceStageRef.current = CHECKOUT_BLOCKED_STAGES.CHECKOUT
      setStockShortages([{
        productId: insufficientStockLine.shortProductId ?? null,
        productName: insufficientStockLine.kind === 'ingredient' ? insufficientStockLine.ingredientName : insufficientStockLine.productName,
        ingredientOf: insufficientStockLine.kind === 'ingredient' ? insufficientStockLine.productName : null,
        available: insufficientStockLine.available,
        attempted: insufficientStockLine.attempted,
      }])
      // Same reason as the cart-stage guard, different STAGE: this one fires on a cart that was
      // built fine and went stale (ATL-913). Telling the two apart is the point of `stage`.
      reportCheckoutBlocked(CHECKOUT_BLOCKED_REASONS.STOCK_UNAVAILABLE)
      return
    }
    const priceTypeId = selectedPriceType?.id || null
    // ATL-1375: restored/order-seeded carts can carry an old product snapshot with no salePrices
    // even after the live catalogue has loaded the correct normalized per-store row. Repair ONLY
    // that absence from the live product before computing any total. An explicit cart value —
    // including zero or a cashier-edited price — remains authoritative and is never overwritten.
    const pricedCartItems = cartItems.map((value) => {
      if (hasResolvableProductPrice(value, store, priceTypeId)) return value
      const liveProduct = productsDict[value.id]
      return liveProduct && hasResolvableProductPrice(liveProduct, store, priceTypeId)
        ? {
          ...value,
          salePrices: liveProduct.salePrices,
          priceTypeScopes: liveProduct.priceTypeScopes,
        }
        : value
    })
    var cartItemsWithFinalPrices = []
    for (let value of pricedCartItems) {
      var newValue = {
        ...value,
        finalItemPrice: finalItemPrice(value, store, dynamicDiscountOptions[discountSelectedIndex]?.percentage, purchaseMethod?.priceModificators, priceTypeId, priceTypes),
        itemPriceWithoutModifiers: itemPriceWithoutModifiers(value, store, 0, priceTypeId, priceTypes),
        priceType: priceTypeId
      }
      if (newValue.quantity > 0) cartItemsWithFinalPrices.push(newValue)
    }
    if (cartItemsWithFinalPrices.length === 0) {
      setSaveError(t('newSale.cartItemsCannotBeEmpty'))
      setIsLoading(false)
      // The message says "empty cart" but the cart is NOT empty — every line was dropped by the
      // `quantity > 0` filter above. Reported as `invalid_quantity` rather than `cart_empty` so the
      // two are separable in PostHog: one means the cashier sold nothing, the other means a
      // quantity got to zero and the screen's own copy is misleading about it.
      reportCheckoutBlocked(CHECKOUT_BLOCKED_REASONS.INVALID_QUANTITY)
      return
    }
    // ATL-1375: a catalogue line must have an explicit price for this store. `0` is allowed — a
    // merchant may intentionally give an item away — but an ABSENT key means the read path failed
    // to resolve the normalized `product_store_prices` row. Letting that absence fall through the
    // pricing helpers manufactures zero and permanently records a zero-total sale. Resolve against
    // the live catalogue rather than the cart snapshot so a broken realtime refresh is caught too.
    const lineWithoutStorePrice = cartItemsWithFinalPrices.find((line) => {
      if (!productsDict[line.id]) return false // custom/modifier lines carry their own explicit price
      return !hasResolvableProductPrice(line, store, priceTypeId)
    })
    if (lineWithoutStorePrice) {
      setSaveError(t('newSale.priceMissingError'))
      setIsLoading(false)
      reportCheckoutBlocked(CHECKOUT_BLOCKED_REASONS.PRICE_MISSING)
      return
    }
    // ATL-867: same money-input parse as saveCart. The sale path used a bare `parseFloat`, which
    // turns a cleared discount box ('') into NaN — a value that would be written straight into the
    // books and refused by the API's `z.number()`. Parse once, refuse what cannot be read.
    const saleDiscountAmount = parseMoneyInput(discountAmount)
    if (saleDiscountAmount === null) {
      setSaveError(t('notANumberError'))
      setIsLoading(false)
      reportCheckoutBlocked(CHECKOUT_BLOCKED_REASONS.INVALID_DISCOUNT_AMOUNT)
      return
    }
    var cartInfo = {
      totalCartPrice: totalCartPrice(pricedCartItems, store, priceTypeId, priceTypes),
      totalCartPriceWithPriceModificators: totalCartPriceWithPriceModificators(pricedCartItems, store, purchaseMethod?.priceModificators, priceTypeId, priceTypes),
      finalTotalCartPrice: finalTotalCartPrice(dynamicDiscountOptions[discountSelectedIndex]?.percentage, saleDiscountAmount, pricedCartItems, store, client, bonusAction, constants?.loyaltySettings?.cashbackEnabled, constants?.loyaltySettings?.punchCardEnabled, constants?.loyaltySettings?.punchCardCapacity, purchaseMethod?.priceModificators, priceTypeId, priceTypes),
      priceType: priceTypeId,
      priceTypes: priceTypes,
      totalPurchasePrice: totalCartPurchasePrice(pricedCartItems),
      totalCartQuantity: totalCartQuantity(pricedCartItems),
      discountPrice: discountPrice(dynamicDiscountOptions[discountSelectedIndex]?.percentage, saleDiscountAmount, pricedCartItems, store, priceTypeId, priceTypes),
      cashierUID: context.user.uid,
      cartItems: cartItemsWithFinalPrices,
      comment,
      tableNumber,
      paymentMethod: paymentMethod,
      purchaseMethod: purchaseMethod,
      priceModificators: purchaseMethod?.priceModificators || null,
      store: store,
      organizationId: context.user.organizationId,
      saleDate: saleDate ? saleDate : new Date(),
      createdDate: new Date(),
      dailyStatID: getDailyStatID(store, saleDate ? saleDate : new Date()),
      discountPercentage: dynamicDiscountOptions[discountSelectedIndex]?.percentage ?? 0,
      discountAmount: saleDiscountAmount
    }
    if (mixedPayment) {
      // ATL-1371 — the split-payment first leg. The keypad here was already `decimal-pad`, so the
      // defect was only ever in the read: `parseFloat('500abc')` is 500 and `parseFloat('1.2.3')`
      // is 1.2, neither of them NaN, so the guard below never fired and the sale committed 500 to
      // one wallet and `total − 500` to the other. Same message, same blocked reason — only what
      // counts as readable has changed.
      const numberAmount = parseDecimalAmount(firstPaymentValue)
      if (numberAmount === null) {
        setIsLoading(false)
        setSaveError(t('newSale.mixedPaymentFirstPaymentNotNumber'))
        reportCheckoutBlocked(CHECKOUT_BLOCKED_REASONS.INVALID_PAYMENT_AMOUNT)
        return
      }
      // UNREACHABLE, and left exactly as it was. `parseFloat('')` is NaN, so an empty first amount
      // has already returned above — this branch's own message has never been shown. It is
      // instrumented anyway and with the SAME reason, so that removing the `isNaN` guard (or
      // reordering the two) cannot silently create an unreported refusal. Deleting dead code is a
      // behaviour change and this diff is analytics-only; it is noted in the PR instead.
      if (mixedPayment && firstPaymentValue === '') {
        setSaveError(t('newSale.mixedPaymentFirstPaymentAmountEmpty'))
        setIsLoading(false)
        reportCheckoutBlocked(CHECKOUT_BLOCKED_REASONS.INVALID_PAYMENT_AMOUNT)
        return
      }
      if (mixedPayment && !secondPaymentMethod) {
        setSaveError(t('newSale.mixedPaymentSecondPaymentMethodEmpty'))
        setIsLoading(false)
        reportCheckoutBlocked(CHECKOUT_BLOCKED_REASONS.SECOND_PAYMENT_METHOD_MISSING)
        return
      }
      if (paymentMethod?.id === secondPaymentMethod.id) {
        setSaveError(t('newSale.mixedPaymentSecondPaymentIsSameAsFirst'))
        setIsLoading(false)
        reportCheckoutBlocked(CHECKOUT_BLOCKED_REASONS.SECOND_PAYMENT_METHOD_DUPLICATE)
        return
      }
      cartInfo.mixedPayment = true
      cartInfo.secondPaymentMethod = {...secondPaymentMethod, 
        value: (finalTotalCartPrice(dynamicDiscountOptions[discountSelectedIndex]?.percentage, saleDiscountAmount, pricedCartItems, store, client, bonusAction, constants?.loyaltySettings?.cashbackEnabled, constants?.loyaltySettings?.punchCardEnabled, constants?.loyaltySettings?.punchCardCapacity, purchaseMethod?.priceModificators, priceTypeId, priceTypes) - numberAmount)
      }
      cartInfo.paymentMethod = {...paymentMethod, value: numberAmount }
    }
    if (delivery) {
      // ATL-1371 — the delivery price, read the same lax way as the split payment above.
      const numberAmount = parseDecimalAmount(deliveryPrice)
      if (numberAmount === null) {
        setIsLoading(false)
        setSaveError(t('newSale.delvieryPriceNotANumberError'))
        reportCheckoutBlocked(CHECKOUT_BLOCKED_REASONS.INVALID_DELIVERY_PRICE)
        return
      }
      if (!deliveryPaidByCustomer && !deliveryPaymentSourceWallet) {
        setIsLoading(false)
        setSaveError(t('newSale.deliveryPaymentSourceEmptyError'))
        reportCheckoutBlocked(CHECKOUT_BLOCKED_REASONS.DELIVERY_PAYMENT_SOURCE_MISSING)
        return
      }
      if (!deliveryHasDestination({ delivery, deliveryAddress, client })) {
        setIsLoading(false)
        setSaveError(t('newSale.deliveryAddressEmptyError'))
        reportCheckoutBlocked(CHECKOUT_BLOCKED_REASONS.DELIVERY_ADDRESS_MISSING)
        return
      }
      cartInfo = {...cartInfo,
        delivery,
        deliveryPrice: numberAmount,
        deliveryPaidByCustomer,
        deliveryAddress,
        deliveryIncludeInReceipt,
      }
      if (!deliveryPaidByCustomer) {
        cartInfo = {...cartInfo, deliveryPaymentSourceWallet}
      }
    }
    if (client != null) {
      cartInfo = {
        ...cartInfo,
        client: {...client},
        clientId: client.id,
        clientPhone: client.phoneNumber,
        bonusAction: bonusAction,
        cashbackEnabled: (constants?.loyaltySettings?.cashbackEnabled || false),
        bonusReduction: bonusReductionValue(client, bonusAction, dynamicDiscountOptions[discountSelectedIndex]?.percentage, saleDiscountAmount, pricedCartItems, store, constants?.loyaltySettings?.cashbackEnabled, purchaseMethod?.priceModificators, priceTypeId, priceTypes),
        bonusAccumulation: bonusAccumulationValue(client, bonusAction, dynamicDiscountOptions[discountSelectedIndex]?.percentage, saleDiscountAmount, pricedCartItems, store, constants?.loyaltySettings?.cashbackEnabled, purchaseMethod?.priceModificators, constants?.loyaltySettings?.punchCardEnabled, constants?.loyaltySettings?.punchCardCapacity, priceTypeId, priceTypes),
        punchCardEnabled: (constants?.loyaltySettings?.punchCardEnabled || false),
        punchCardReduction: punchCardReduction(client, dynamicDiscountOptions[discountSelectedIndex]?.percentage, saleDiscountAmount, pricedCartItems, store, constants?.loyaltySettings?.punchCardEnabled, constants?.loyaltySettings?.punchCardCapacity, purchaseMethod?.priceModificators, priceTypeId, priceTypes),
        punchCardCapacity: constants?.loyaltySettings?.punchCardCapacity
      }
    }
    if (postPaid) {
      cartInfo['postPaid'] = true
      cartInfo['paymentMethod'] = null
    }
    setCartItems([])
    setPaymentMethod(null)
    setClient(null)
    setBonusAction(0)
    setPostPaid(null)
    setMixedPayment(null)
    setSecondPaymentMethod(null)
    setFirstPaymentValue('')
    setDiscountSelectedIndex(0)
    setDiscountAmount(0)
    setSavedCartApplied(null)
    // Taken now, before the save: whatever happens next, the NEXT sale is never this draft's.
    const appliedApiSaleDraft = apiSaleDraftApplied
    setApiSaleDraftApplied(null)
    setComment('')
    setTableNumber('')
    setIsLoading(false)
    setDelivery(null)
    setDeliveryPrice(currentStoreSaleSettings?.deliveryPrice)
    setDeliveryAddress('')
    setDeliveryPaidByCustomer(currentStoreSaleSettings?.deliveryPaidByCustomer ?? true)
    setDeliveryIncludeInReceipt(currentStoreSaleSettings?.deliveryIncludeInReceipt ?? true)
    setDeliveryPaymentSourceWallet(currentStoreSaleSettings?.deliveryPaymentSourceWallet)
    setPaymentModalVisible(false)
    setManualPriceTypeSelection(false)
    let ref
    // ATL-1644 — what this sale's Supabase dual-write did, for `sale_created` below.
    let dualWriteOutcome
    try {
      ref = await saveNewSale(db, cartInfo, { onDualWriteOutcome: (outcome) => { dualWriteOutcome = outcome } })
      // ATL-1445: note and report the draft's sale the moment it is committed — BEFORE the receipt
      // and fiscal work below, which can await a native print dialog. Until the device note is
      // written the draft still reads "open" everywhere, so an app killed mid-print would offer the
      // same draft again and a second tap would be a second real sale.
      if (appliedApiSaleDraft) {
        navigation.setParams({ apiSaleDraft: undefined })
        void reportApiDraftSaved({
          draft: appliedApiSaleDraft,
          organizationId: context.user.organizationId,
          firestoreId: ref.id,
          onRefused: (code) => toast.show(t('apiDrafts.linkRefused', { reason: t(apiDraftRefusalKey(code)) }), {
            type: 'warning', placement: 'bottom', duration: 6000,
          }),
        })
      }
      // ATL-1274: «Успешно» claimed the whole operation. What has actually happened at this line is
      // that the sale is durably saved — the fiscal receipt is not even attempted until below, is
      // not awaited, and may well be refused. Naming only what is true is the point of the ticket:
      // a cashier who sees this and then a fiscal-failure toast is being told two accurate things,
      // not one contradictory one.
      toast.show(t('newSale.saleSaved'), {
        type: "custom",
        placement: "bottom",
        duration: 3000,
        offset: 30,
        animationType: "slide-in",
      })
    } catch(error) {
      Sentry.captureException(error, { tags: { books_write_error: 'sale-create' } })
      console.log(error)
      if (error.message === 'INGREDIENT_NOT_FOUND') {
        setSaveError(t('ingredientNotFoundError', {
          name: error.ingredientName,
          product: error.productName
        }))
      } else {
        setSaveError(saveFailureText(error, t, t('error'), 'sale'))
      }
      // ATL-1319 — the one refusal that is NOT a validation branch: the write itself was refused,
      // and until now the whole class was one untyped `t('error')`. `classifySaveFailure` reads the
      // error only to pick a bucket; the message never leaves the device, because a thrown message
      // on this path can carry a product name, a client name or an address.
      //
      // LAST in the handler, after the message is on screen, which is the order every other call
      // site already uses. Nothing on this line can throw today — both `classifySaveFailure` and
      // `captureCheckoutBlocked` are total over any input — but this is the one branch where a
      // reporter that DID throw would swallow the cashier's error message as well, and ATL-901 is
      // the standing reminder that telemetry gets to be last.
      reportCheckoutBlocked(classifySaveFailure(error))
    }
    if (savedCartApplied) {
      const itemId = savedCartApplied.id || savedCartApplied.ref?.id
      setContext(ctx => ({...ctx, savedCarts: (ctx.savedCarts || []).filter(c => (c.id || c.ref?.id) !== itemId)}))
      deleteSavedCart(savedCartApplied)
    }
    if (ref) {
      // ATL-1020: `?.fiscalOperator?.id !== 'none'` read an ABSENT operator as "fiscalization is
      // configured", so a store with no operator took the OFD branch — which then matched neither
      // 'light-kassa' nor 'wipon-kassa' and did nothing at all. The internal receipt below was
      // never printed, silently, for every printChecks store whose operator is null.
      if (isFiscalizationEnabled(storesDict?.[store]) && !postPaid) {
        // Deliberately not awaited — the cashier must not wait on the OFD. But an unawaited
        // rejection is invisible, which is the ATL-1170 failure mode one level up: attach a
        // handler so anything that throws past `fiscalizeSale` still reaches Sentry.
        // Two ARGUMENTS to `.then`, not `.then(...).catch(...)`: a chained `catch` also catches
        // whatever the fulfilment handler throws, so a `toast.show` that failed after a refusal
        // had already been reported would report the SAME sale a second time — a second toast for
        // one sale, and a Sentry exception describing the reporter rather than the OFD.
        saveSaleToOFD(cartInfo, ref)
          .then(
            (outcome) => {
              if (outcome && !outcome.fiscalized) reportFiscalizationFailure(outcome)
              // ASO-RATING-2026-001 — a fiscalizing store's sale is complete only once it is SIGNED
              // and its receipt printed. A refusal, or a print failure the cashier was just shown,
              // is an error state and never a moment to ask for a rating — and neither is a signed
              // sale landing while the unsigned-receipts banner is up (an earlier sale, possibly the
              // one rung up after this, was refused; `reportFiscalizationFailure` updates the ref
              // synchronously, so a refusal that landed first is already visible here).
              else if (outcome?.fiscalized && outcome.receiptPrinted && unfiscalizedRef.current.length === 0) {
                void requestAppReviewAtMilestone(APP_REVIEW_TRIGGERS.SALE_SAVED, postHog)
              }
            },
            (error) => {
              Sentry.captureException(error, { tags: { books_write_error: 'sale-fiscalize' } })
              // An exception past `saveSaleToOFD` means we do not know that anything was signed, and
              // "we do not know" belongs in the same place as "it was refused" — not in Sentry alone,
              // where the merchant cannot see it.
              reportFiscalizationFailure({reason: 'unexpected-error', message: null, saleId: unfiscalizedSaleKey(ref)})
            },
          )
          // The two-argument `.then` above deliberately does not use a chained `.catch` (see the
          // comment on it), but that left NOTHING watching the handlers themselves: a `toast.show`
          // that threw after a refusal was reported became an unhandled rejection, where before
          // this change it reached Sentry. This trailing `catch` can only ever see a throw from
          // one of those two handlers — the OFD outcome has already been reported by then — so it
          // restores the capture without being able to report the same sale twice.
          .catch(error => Sentry.captureException(error))
      } else {
        // ASO-RATING-2026-001 — see the fiscal branch above: a receipt that did not print is not a
        // completed sale from the cashier's point of view, and the unsigned-receipts banner is an
        // error state on screen. Web never prompts (no App Store), so its print path, which reports
        // no outcome, does not need one. A POSTPAID sale at a fiscalizing store lands here too: it is
        // a committed sale with nothing to sign yet, which is a completed core action.
        let receiptPrinted = true
        if (printChecks) {
          let receipt = generateInternalReceipt(cartInfo, organization?.name, storesDict?.[cartInfo.store]?.name, usersDict[cartInfo.cashierUID]?.name, getLocalStoreDate(cartInfo.store, cartInfo.saleDate instanceof Date ? cartInfo.saleDate : cartInfo.saleDate.toDate()), currencyFormatter, productUnitsDict, t('pcs'), storesDict?.[cartInfo.store]?.address) 
          if (Platform.OS === 'web') {
            let html = '<html><head><title>' + t('newSale.receipt') + '</title>' + '<style> div {white-space:pre-wrap;} </style>' + '</head><body >'
              + receipt + '</body></html>'
            let mywindow = window.open('', 'PRINT', 'height=400,width=600')
            if (mywindow) {
              mywindow.document.write(html)
              mywindow.document.close()
              mywindow.focus()
              mywindow.onafterprint = function() {mywindow.close()}
              mywindow.print()
            }
          } else {
            let html = '<html><head><title>' + t('newSale.receipt') + '</title>' + '<style> div {white-space:pre-wrap;} </style>' + '</head><body >'
            + receipt + '</body></html>'
            receiptPrinted = await safePrintAsync({ html }, { toast, t })
          }
        }
        if (receiptPrinted && unfiscalizedRef.current.length === 0) {
          void requestAppReviewAtMilestone(APP_REVIEW_TRIGGERS.SALE_SAVED, postHog)
        }
      }
    }
    // ATL-1319 — `sale_created` must mean a sale EXISTS.
    //
    // These two lines were outside the `if (ref)` below them, and after the try/catch: a write that
    // THREW — an offline till, a 401, an incomplete composite — set the error message, saved
    // nothing, and then reported a sale to both analytics backends anyway. The population that
    // over-counted is exactly the population `sale_checkout_blocked` was added to find, so leaving
    // it would have produced a funnel that disagreed with itself: a refusal in one event and a sale
    // in the other, for the same tap.
    //
    // `ref` is the best gate available: it is what `saveNewSale` resolves to, so it is set only if
    // the sale committed. The converse is NOT quite true, and the comment used to claim it was.
    // `saveNewSale` runs post-commit work outside any try (`setTimezone(info.store)` at
    // global/Firebase/sales.ts:1022, which dereferences `storesDict[storeId].timezone` unguarded,
    // then the payload build). A throw there leaves `ref` undefined for a sale that EXISTS, and
    // this gate then under-counts it — and the catch reports `unknown` for a completed sale.
    //
    // Gating is still right: that path needs a store missing from the dict, while the failures it
    // stops — offline, 401, bad composite — are the ordinary ones. Trading a rare under-count for a
    // routine over-count is the correct direction. The narrow case is a real defect of its own
    // (the cashier is already shown `t('error')` for a committed sale, which predates this change)
    // and is filed as ATL-1320, not fixed in an analytics diff.
    //
    // The refusal path is not silent in exchange; it emits `sale_checkout_blocked` with the
    // classified reason, from the catch above.
    if (ref) {
      sendFBAnalyticsEvent('new_sale')
      // ATL-1644: the sale's id and its dual-write outcome, so a sale missing from Supabase can be
      // traced to the path it took (lost, queued, gate-denied) instead of being unexplainable.
      postHog.capture('sale_created', saleCreatedProperties(ref.id, dualWriteOutcome))
    }
  }, [navigation, apiSaleDraftApplied, userRole, context.user.role, dailyStatsShiftStatus, store, purchaseMethod, cartItems, productsDict, paymentMethod, postPaid, t, toast, postHog, constants, newSaleInterfaceSettings, currentStoreSaleSettings, currencyFormatter, productUnitsDict, organization, storesDict, usersDict, localeDateService, db, saveNewSale, updateSale, saveFiscalOperation, setNewLKTokens, refreshCredentials, getProfiles, generateInternalReceipt, getLocalStoreDate, getDailyStatID, sendFBAnalyticsEvent, dynamicDiscountOptions, discountSelectedIndex, discountAmount, client, bonusAction, loyaltySettings, mixedPayment, firstPaymentValue, secondPaymentMethod, delivery, deliveryPrice, deliveryPaidByCustomer, deliveryAddress, deliveryIncludeInReceipt, deliveryPaymentSourceWallet, comment, tableNumber, saleDate, printChecks, savedCartApplied, reportCheckoutBlocked])

  return (
    <SafeAreaView style={{flex: 1, width: '100%'}}>
      <KeyboardAvoidingView style={{flex: 1, width: '100%'}} behavior="height" keyboardVerticalOffset = {100}>
        {/*
          ATL-1274 — the persistent, non-blocking half of a fiscal failure. It replaces a modal
          `alert()` per sale, so it has to be impossible to miss WITHOUT being impossible to work
          through: it sits above the whole screen, it says the sales are saved (they are), it
          counts them, and its call to action is the screen that can re-sign them. It clears when
          those sales are signed, from either retry path in Sales History.
          `accessibilityLiveRegion` is what makes the count change reach a screen reader, since
          nothing here takes focus.
        */}
        {apiSaleDraftApplied && (
          <View testID="api-sale-draft-banner" style={{paddingHorizontal: 20, paddingTop: 10}}>
            <HintView
              iconName='file-text-outline'
              text={t('apiDrafts.saleBanner', { name: apiSaleDraftApplied.proposedBy?.tokenName || t('apiDrafts.agent') })}
            />
          </View>
        )}
        {/* ATL-1445 P9b: «N операций не синхронизированы» — renders nothing without a session drainer. */}
        <SessionOutboxV2Banner organizationId={context.user?.organizationId} />
        {unfiscalizedSales.length > 0 && (
          <View
            testID="unfiscalized-sales-banner"
            accessibilityLiveRegion="polite"
            style={{paddingHorizontal: 20, paddingTop: 10}}
          >
            <HintView
              appearance='danger'
              iconName='alert-triangle-outline'
              text={t('newSale.unfiscalizedSalesInSession', {count: unfiscalizedSales.length})}
              action={() => navigation.navigate('SalesHistory')}
              callToAction={t('newSale.openSalesHistory')}
            />
          </View>
        )}
        <View style={styles.splitView}>
          <View style={{flex: Dimensions.get('window').width >= 500 ? (parseInt(newSaleInterfaceSettings?.gridLeftWidth) || 5) : 5}}>
            <ScrollView style={{flex: 1, padding: 20}} keyboardShouldPersistTaps='handled'>
              { Dimensions.get('window').width < 500 && (
                <Button style={styles.addButton} 
                  onPress={() => {
                    navigation.navigate('AddCartItem', {
                      addNewCartItem,
                      store,
                      cartItems
                    })
                  }} 
                  status='primary' 
                  appearance='outline' 
                  accessoryLeft={(props) => (
                    <Icon name='plus-outline' {...props} />
                  )}
                >
                  {t('newSale.addProduct')}
                </Button>
              )}
              <View style={{flexDirection: 'row', justifyContent: 'space-between', marginBottom: 15, gap: 5}}>
                <Button appearance='outline'
                  status='basic'
                  style={{flex: 1, paddingHorizontal: 0}}
                  accessoryLeft={(props) => (<Icon name='book-open-outline' {...props} style={[props.style, {marginRight: -10, marginTop: Platform.OS === 'web' ? 4 : 1}]} />)}
                  onPress={() => {
                    setCartMenuVisible(true)
                  }}
                  testID="saved-carts-button"
                >
                  {'(' + (context.savedCarts?.length || 0) + ')'}
                </Button>
                <Button appearance='outline' 
                  status='basic'
                  style={{flex: 1, paddingHorizontal: 0}} 
                  accessoryLeft={(props) => (<Icon name='settings-2-outline' {...props} />)}
                  onPress={() => {
                    setSettingsModalVisible(true)
                  }}
                />
                {/* Price Type Selector */}
                {priceTypes && priceTypes.length > 0 && (
                  <PriceTypeSelect
                    selectedPriceType={selectedPriceType}
                    onSelect={handlePriceTypeSelect}
                    priceTypes={priceTypes}
                    cartItems={cartItems}
                    store={store}
                    style={{flex: 1, minWidth: '25%'}}
                  />
                )}
                { (userRole?.permissions?.['applyDiscount'] || context.user.role === 'admin') ? (
                  <DiscountSelect style={{flex: 1, minWidth: '32%'}} navigation={navigation} discountOptions={dynamicDiscountOptions} discountSelectedIndex={discountSelectedIndex} discountAmount={discountAmount} discountMenuVisible={discountMenuVisible} setDiscountMenuVisible={setDiscountMenuVisible} setDiscountSelectedIndex={setDiscountSelectedIndex} setDiscountAmount={setDiscountAmount} />
                ) : (
                  <Button appearance='outline' 
                    status='basic'
                    style={{flex: 1, minWidth: '25%'}} 
                    disabled={true}
                  />
                )}
              </View>
              <Modal style={[styles.discountModal, {backgroundColor: theme['background-basic-color-1']}]}
                visible={showDiscountAmountInputPopup}
                backdropStyle={styles.backdrop} 
                onBackdropPress={() => {
                  setShowDiscountAmountInputPopup(false)
                  setDiscountSelectedIndex(0)
                }}
              >
                <TouchableOpacity 
                  style={{position: 'absolute', top: 10, right: 10, alignSelf: 'flex-end', alignItems: 'center', justifyContent: 'center'}} 
                  onPress={() => {
                    setShowDiscountAmountInputPopup(false)
                    setDiscountSelectedIndex(0)
                  }}
                > 
                  <Icon name='close-circle' width={32} height={32} fill={theme['text-basic-color']} />
                </TouchableOpacity>
                <Text category='h5' style={{marginTop: 20}}>{t('newSale.discountAmountPopupTitle')}</Text>
                <StyledTextInput 
                  value={discountAmount}
                  onChangeText={text => setDiscountAmount(text.replace(/,/g, '.'))}
                  keyboardType='decimal-pad'
                  style={{marginBottom: 10, marginTop: 20}}
                  returnKeyType='done'
                  onSubmitEditing={() => setShowDiscountAmountInputPopup(false)}
                  autoFocus={true}
                  selectTextOnFocus={true}
                />
                <Button style={{marginBottom: 20}} onPress={() => {
                  setShowDiscountAmountInputPopup(false)
                }}>{t('save')}</Button>
              </Modal>
              <StockShortageModal
                shortages={stockShortages}
                canReceive={canReceiveStock}
                isReceivable={isStockShortageReceivable}
                onReceive={openStockReceiving}
                onClose={closeStockShortages}
                onShown={reportStockGuidanceShown}
              />
              <SavedCartsModal db={db} cartMenuVisible={cartMenuVisible} setCartMenuVisible={setCartMenuVisible}
                savedCarts={context.savedCarts} users={usersDict} onDeleteCart={handleDeleteSavedCart}
                setCartItems={setCartItems} setClient={setClient}
                discountOptions={dynamicDiscountOptions} setDiscountSelectedIndex={setDiscountSelectedIndex} setDiscountAmount={setDiscountAmount}
                setBonusAction={setBonusAction}
                setSavedCartApplied={setSavedCartApplied} currencyFormatter={currencyFormatter}
                setTableNumber={setTableNumber} setComment={setComment}
                setDelivery={setDelivery} setDeliveryPrice={setDeliveryPrice} 
                setDeliveryPaidByCustomer={setDeliveryPaidByCustomer} 
                setDeliveryIncludeInReceipt={setDeliveryIncludeInReceipt} 
                setDeliveryAddress={setDeliveryAddress}
                setDeliveryPaymentSourceWallet={setDeliveryPaymentSourceWallet}
                wallets={constants?.wallets?.data}
                setPaymentMethod={setPaymentMethod}
                setSelectedPriceType={setSelectedPriceType}
                setManualPriceTypeSelection={setManualPriceTypeSelection}
              />
              <NewSaleSettingsModal 
                settingsModalVisible={settingsModalVisible} setSettingsModalVisible={setSettingsModalVisible} 
                interfaceSettings={newSaleInterfaceSettings}
                navigation={navigation} 
              />
              { currentStoreSaleSettings?.dateFieldVisible && (
                <>
                  <Text category='label' style={{marginBottom: 5}}>{t('newSale.saleDate')}</Text>
                  <Datepicker
                    placement='top'
                    date={saleDate}
                    dateService={localeDateService}
                    onSelect={(date) => {
                      let currentDate = new Date()
                      date.setHours(currentDate.getHours(), currentDate.getMinutes())
                      setSaleDate(date)
                      _datePicker.current.blur()
                    }}
                    min={new Date('2020-12-01')}
                    max={new Date('2029-12-31')}
                    ref={_datePicker}
                    style={{marginBottom: 10}}
                  />
                </>
              )}
              { currentStoreSaleSettings?.storeFieldVisible && (
                <>
                  <Text category='label' style={{marginBottom: 5}}>{t('store')}</Text>
                  <PlatformDependantSelect
                    data={Object.values(storesDict)}
                    selectedOption={storesDict?.[store]}
                    onSelect={(itemValue, itemIndex) => {
                      setStore(itemValue.id)
                    }}
                    style={{marginBottom: 10}}
                  />
                </>
              )}
              { currentStoreSaleSettings?.tableNumberFieldVisible && (
                <>
                  <Text category='label' style={{marginBottom: 5}}>{t('newSale.tableNumber')}</Text>
                  <Input
                    placeholder='1'
                    value={tableNumber}
                    keyboardType='number-pad'
                    onChangeText={setTableNumber}
                    style={{marginBottom: 10}}
                    testID="table-number-input"
                  />
                </>
              )}
              { currentStoreSaleSettings?.commentFieldVisible && (
                <>
                  <Text category='label' style={{marginBottom: 5}}>{t('newSale.comment')}</Text>
                  <Input
                    value={comment}
                    onChangeText={setComment}
                    style={{marginBottom: 10}}
                    testID="comment-input"
                  />
                </>
              )}
              <View style={{flexDirection: 'row', alignItems: 'center', marginBottom: 5}}>
                <Text category='label' style={{marginRight: 5}}>{t('newSale.client')}</Text>
                <TouchableOpacity onPress={() => setCreateClientModalVisible(true)} testID="create-client-button">
                  <Icon name='plus-circle-outline' height={16} width={16} fill={colors.primary}/>
                </TouchableOpacity>
              </View>
              <Modal style={[styles.modal, {padding: 30, backgroundColor: theme['background-basic-color-1']}]}
                visible={createClientModalVisible}
                backdropStyle={styles.backdrop} 
                onBackdropPress={() => setCreateClientModalVisible(false)}
              >
                <TouchableOpacity 
                  style={{position: 'absolute', top: 10, right: 10, alignSelf: 'flex-end', alignItems: 'center', justifyContent: 'center'}} 
                  onPress={() => {
                    setCreateClientModalVisible(false)
                  }}
                > 
                  <Icon name='close-circle' width={32} height={32} fill={theme['text-basic-color']} />
                </TouchableOpacity>
                <CreateClientView db={context.db} defaultBonusLevel={constants?.loyaltySettings?.defaultBonusLevel} 
                  store={store} storeInfo={storesDict?.[store]} organizationId={context.user.organizationId} onCreate={(client) => {
                    setClient(client)
                    setCreateClientModalVisible(false)
                  }}
                />
              </Modal>
              <ClientAutoComplete style={{marginBottom: 10}} db={context.db} organizationId={context.user.organizationId} onSelect={setClient} defaultValue={clientOrderDetail?.phone} />
              
              { client && (<ClientView 
                clientValue={client} clearClient={clearClient}
                bonusAction={bonusAction} setBonusAction={setBonusAction} 
                cashbackEnabled={loyaltySettings.cashbackEnabled} 
                punchCardEnabled={loyaltySettings.punchCardEnabled} 
                punchCardCapacity={loyaltySettings.punchCardCapacity}
                priceTypes={priceTypes} 
                currencyFormatter={currencyFormatter} style={{marginBottom: 10}}
              /> )}
              <CartView 
                style={{marginTop: 6, marginBottom: 16}}
                cartItems={cartItems} 
                discountPercentage={dynamicDiscountOptions[discountSelectedIndex]?.percentage} 
                discountAmount={discountAmount}
                purchaseMethod={purchaseMethod}
                cashbackEnabled={loyaltySettings.cashbackEnabled}
                punchCardEnabled={loyaltySettings.punchCardEnabled}
                punchCardCapacity={loyaltySettings.punchCardCapacity}
                client={client}
                bonusAction={bonusAction}
                priceType={selectedPriceType?.id || null}
                onItemPress={useCallback((item, index) => {
                  navigation.navigate('EditCartItem', {index: index, cartItem: item, cartItems, setCartItems, store: store, addNewCartItem: addNewCartItem})
                }, [navigation, cartItems, setCartItems, store, addNewCartItem])} 
                store={store} 
                changeQuantityOfItem={changeQuantityOfItem}
              />
              
              { newSaleInterfaceSettings?.checkoutStyle == 2 ? (
                <Modal style={[styles.modal, {backgroundColor: theme['background-basic-color-1']}]}
                  visible={paymentModalVisible}
                  backdropStyle={styles.backdrop} 
                  onBackdropPress={() => setPaymentModalVisible(false)}
                >
                  <ScrollView style={{padding: 30}}>
                    <PaymentView 
                      purchaseMethod={purchaseMethod} setPurchaseMethod={setPurchaseMethod} purchaseMethods={constants?.purchaseMethods?.data} paymentMethod={paymentMethod}  
                      setPaymentMethod={setPaymentMethod} paymentMethods={constants?.paymentMethods?.data} mixedPayment={mixedPayment} setMixedPayment={setMixedPayment} firstPaymentValue={firstPaymentValue} setFirstPaymentValue={setFirstPaymentValue}
                      secondPaymentMethod={secondPaymentMethod} setSecondPaymentMethod={setSecondPaymentMethod} postPaid={postPaid} setPostPaid={setPostPaid} user={context.user} printChecks={printChecks} setPrintChecks={setPrintChecks} 
                      client={client} saveError={saveError} isLoading={isLoading} submit={submit} checkoutStyle={newSaleInterfaceSettings?.checkoutStyle} cartItems={cartItems} discountOptions={dynamicDiscountOptions} discountSelectedIndex={discountSelectedIndex} discountAmount={discountAmount}
                      db={db} saveCart={saveCart} setPaymentModalVisible={setPaymentModalVisible} bonusAction={bonusAction} currencyFormatter={currencyFormatter} loyaltySettings={loyaltySettings}
                      delivery={delivery} setDelivery={setDelivery} deliveryPrice={deliveryPrice} setDeliveryPrice={setDeliveryPrice} 
                      deliveryPaidByCustomer={deliveryPaidByCustomer} setDeliveryPaidByCustomer={setDeliveryPaidByCustomer} 
                      deliveryIncludeInReceipt={deliveryIncludeInReceipt} setDeliveryIncludeInReceipt={setDeliveryIncludeInReceipt} 
                      deliveryAddress={deliveryAddress} setDeliveryAddress={setDeliveryAddress}
                      deliveryPaymentSourceWallet={deliveryPaymentSourceWallet} setDeliveryPaymentSourceWallet={setDeliveryPaymentSourceWallet}
                      wallets={constants?.wallets?.data} store={store} setCartItems={setCartItems}
                      selectedPriceType={selectedPriceType} priceTypes={priceTypes}
                    />
                  </ScrollView>
                </Modal>
              ) : (
                <PaymentView 
                  purchaseMethod={purchaseMethod} setPurchaseMethod={setPurchaseMethod} purchaseMethods={constants?.purchaseMethods?.data} paymentMethod={paymentMethod}  
                  setPaymentMethod={setPaymentMethod} paymentMethods={constants?.paymentMethods?.data} mixedPayment={mixedPayment} setMixedPayment={setMixedPayment} firstPaymentValue={firstPaymentValue} setFirstPaymentValue={setFirstPaymentValue}
                  secondPaymentMethod={secondPaymentMethod} setSecondPaymentMethod={setSecondPaymentMethod} postPaid={postPaid} setPostPaid={setPostPaid} user={context.user} printChecks={printChecks} setPrintChecks={setPrintChecks} 
                  client={client} saveError={saveError} isLoading={isLoading} submit={submit} checkoutStyle={newSaleInterfaceSettings?.checkoutStyle} cartItems={cartItems} discountOptions={dynamicDiscountOptions} discountSelectedIndex={discountSelectedIndex} discountAmount={discountAmount}
                  db={db} saveCart={saveCart} setPaymentModalVisible={setPaymentModalVisible} bonusAction={bonusAction} currencyFormatter={currencyFormatter} loyaltySettings={loyaltySettings}
                  delivery={delivery} setDelivery={setDelivery} deliveryPrice={deliveryPrice} setDeliveryPrice={setDeliveryPrice} 
                  deliveryPaidByCustomer={deliveryPaidByCustomer} setDeliveryPaidByCustomer={setDeliveryPaidByCustomer} 
                  deliveryIncludeInReceipt={deliveryIncludeInReceipt} setDeliveryIncludeInReceipt={setDeliveryIncludeInReceipt} 
                  deliveryAddress={deliveryAddress} setDeliveryAddress={setDeliveryAddress}
                  deliveryPaymentSourceWallet={deliveryPaymentSourceWallet} setDeliveryPaymentSourceWallet={setDeliveryPaymentSourceWallet}
                  wallets={constants?.wallets?.data} store={store} setCartItems={setCartItems}
                  selectedPriceType={selectedPriceType} priceTypes={priceTypes}
                /> 
              )}
              
              {saveError && <Text style={{marginBottom: 5}} status='danger'>
                {saveError}
              </Text>}
              {isLoading ? (
                <View style={styles.spinner}><Spinner /></View>
              ) : (
                <>
                  {currentStoreSaleSettings?.showSaveCartForLater && (
                    <View testID="save-cart-for-later-button">
                      <Button style={{marginBottom: 10, marginTop: 5}} appearance='outline' accessoryLeft={(props) => (<Icon name='book-open-outline' {...props} />)} onPress={saveCart}>
                        {t('newSale.saveCartForLater')}
                      </Button>
                    </View>
                  )}
                  {cartIsEmpty && (
                    <Text style={{marginBottom: 5}} appearance='hint' testID="checkout-cart-empty-hint">
                      {t('newSale.addProductToCheckout')}
                    </Text>
                  )}
                  { newSaleInterfaceSettings?.checkoutStyle == 2 ? (
                    <Button status='primary' style={styles.submitButton} onPress={() => setPaymentModalVisible(true)} disabled={cartIsEmpty} testID="proceed-to-payment-button">
                      {t('newSale.proceedToPayment')}
                    </Button>
                  ) : (
                    <Button status='primary' style={styles.submitButton} onPress={submit} disabled={cartIsEmpty} testID="done-button">
                      {t('done')}
                    </Button>
                  )}
                </>
              )}
              <View style={{height: 100}} />
            </ScrollView>
          </View>
          { Dimensions.get('window').width >= 500 && (
            <View style={{flex: 10}}>
              <ProductGrid store={store} addNewCartItem={addNewCartItem} />
            </View>
          )} 
        </View>
      </KeyboardAvoidingView>
    </SafeAreaView>
  )
})

export default NewSaleScreen

const styles = StyleSheet.create({
  splitView: {
    flexDirection: 'row',
    flex: 1
  },
  submitButton: {
    marginBottom: 40,
  },
  spinner: {
    marginTop: 15,
    marginBottom: 40,
    alignItems: 'center'
  },
  addButton: {
    marginBottom: 16,
  },
  modal: {
    top: 100,
    width: '80%',
    height: '80%',
    backgroundColor: 'white',
    borderRadius: 20,
  },
  backdrop: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  discountModal: {
    top: 100,
    width: '80%',
    backgroundColor: 'white',
    borderRadius: 20,
    padding: 30
  },
})
